import { useCallback, useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';

import { useQcpIssueTicket } from '@/hooks/useQcpIssueTicket';
import { openQcpSocket, type QcpConnFail } from '@/lib/qcp/socket';
import {
  ClientEvents,
  ServerEvents,
  type Board,
  type Card,
  type ClockPayload,
  type DealPayload,
  type EndedPayload,
  type ErrorPayload,
  type HandSettledPayload,
  type HandStartPayload,
  type MatchResultPayload,
  type PlaceFlSubmit,
  type PlaceSubmit,
  type ProjectedState,
  type RevealPayload,
  type Row,
  type Seat,
} from '@/lib/qcp/protocol';

/**
 * Dopo `hand:settled` il seed della mano dopo parte al tocco di «Prossima mano» o, se nessuno tocca, da solo dopo questo tempo. Il server aspetta il seed di tutti e
 * due i posti e, per default, 45 s (`QCP_SEED_TIMEOUT_MS`): chi non lo manda perde la mano (±6). 15 s resta prudente, e il timer sta qui nell'hook e non nella
 * schermata, quindi il seed parte anche se il resoconto non e a schermo. ⚠️ Un timer NON basta con la pagina in background (una WebView li ferma): per quello il seed
 * parte SUBITO anche quando la pagina si nasconde (`visibilitychange` con `document.hidden`) o si chiude (`pagehide`), v. il suo effetto piu sotto (C1).
 */
export const SEME_AUTOMATICO_MS = 15_000;

/** Un seed del client: 32 byte da `crypto.getRandomValues`, in esadecimale. */
function nuovoSeed(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Default per-seat zero record (A/B) — used to seed the running-net & FL maps. */
const ZERO_AB: Record<Seat, number> = { A: 0, B: 0 };
const FALSE_AB: Record<Seat, boolean> = { A: false, B: false };

export interface QcpMatchSocket {
  connected: boolean;
  /** Full projected game state — null until the first match:state arrives. */
  state: ProjectedState | null;
  /** Cards dealt to this player for the current hand (deck:commit → deal:cards). */
  draw: Card[];
  /** Per-seat banked clock times + whose turn the clock is tracking. */
  clocks: ClockPayload | null;
  /**
   * The server's commit hash for the current deck (used for provably-fair
   * verification). Set on deck:commit and cleared between hands.
   */
  commitHash: string | null;
  /** The deck-reveal payload emitted at end of hand (provably-fair audit). */
  reveal: RevealPayload | null;
  /**
   * Legacy single-hand terminal payload — set when match:ended fires. The
   * match-over trigger has MOVED to `matchResult` (the multi-hand set end); this
   * field is retained because other code (ChinesePokerMatch) still reads it to
   * navigate to the result screen and to gate the resign button. In the
   * multi-hand loop the server may still emit match:ended for the final hand.
   */
  ended: EndedPayload | null;
  /** Transient server error — auto-cleared after 4 s. */
  error: ErrorPayload | null;
  /**
   * Why there is no socket (ticket refused / capability off / address not allowed / handshake refused / dropped too often), or
   * null. Terminal until `retry()`: a new ticket and a new socket. Never carries the ticket, the address or the raw message.
   */
  connFail: QcpConnFail | null;
  /** The «Riprova» of `connFail`: asks a NEW ticket and opens a NEW socket. */
  retry: () => void;
  /**
   * This client's seat identity, derived from the first match:state projection:
   * `mySeat` is the seat key NOT present in `state.opponents` (the projection
   * only includes the *opponent's* masked entry — see project.ts). Null until
   * the first state arrives.
   */
  mySeat: Seat | null;

  /* ── Multi-hand "set" loop state ─────────────────────────────────────── */

  /** 1-based index of the current hand within the set (from hand:start). */
  handNumber: number;
  /** Cumulative net score across the set so far (from hand:settled running net). */
  runningNet: Record<Seat, number>;
  /** Per-seat Fantasyland flag for the CURRENT hand (from hand:start). */
  inFantasyland: Record<Seat, boolean>;
  /** Per-seat Fantasyland card count for the CURRENT hand (0 = normal seat). */
  flCardCount: Record<Seat, number>;
  /**
   * The terminal match-end signal — set ONCE when match:result fires. This is
   * the canonical "the set is over" trigger for the multi-hand loop (it carries
   * final net, winner, hands played, reason and ELO deltas).
   */
  matchResult: MatchResultPayload | null;
  /**
   * The most recent hand:settled payload — exposes the last reveal + per-hand
   * result for an optional inter-hand summary screen. Null until the first hand
   * settles.
   */
  handSettled: HandSettledPayload | null;
  /**
   * Il resoconto della mano e APERTO: da `hand:settled` fino a `prossimaMano()` (o allo scadere dei `SEME_AUTOMATICO_MS`). Finche e aperto il seed della mano dopo
   * NON parte su `deck:commit`: lo tiene l'hook. Non dipende da `hand:start` (che arriva nello stesso istante di `hand:settled`: staging 17/10, hand:settled e
   * hand:start a 0 ms di distanza): un resoconto che si toglieva con `hand:start` non aveva mai un frame.
   */
  resocontoAperto: boolean;
  /** L'ora (ms epoch) a cui il seed parte da solo, o `null` (nessun conto: niente resoconto aperto, o la partita e finita e nessuna mano segue). */
  prossimaManoEntro: number | null;
  /**
   * «Prossima mano»: chiude il resoconto e manda il seed della mano dopo (se il `deck:commit` e gia arrivato; altrimenti parte da solo quando arriva). Una volta
   * sola per mano. A partita finita (`matchResult`) non c'e nessun seed da mandare: chiude e basta.
   */
  prossimaMano: () => void;
  /**
   * Il seed e partito dopo un resoconto e la distribuzione non e ancora arrivata (`deal:cards`): il tavolo aspetta l'avversario. Falso nella prima mano della
   * partita (il seed li parte da solo, come sempre) e a distribuzione fatta.
   */
  attesaDistribuzione: boolean;

  /**
   * Emit deck:clientSeed. Normally called automatically by the hook once per
   * hand (on deck:commit / hand:start, provably-fair handshake). Exposed so the
   * UI can trigger a manual re-send if needed (rare edge-case; the hook guards
   * against a second automatic emit within the same hand but allows an explicit
   * call).
   */
  submitSeed: (seed: string) => void;
  /** Emit place:submit with this client's placement + optional discard. */
  place: (placements: PlaceSubmit['placements'], discard: Card | null) => void;
  /**
   * Emit place:fl — a Fantasyland seat sets its full 13-card board (front 3 /
   * middle 5 / back 5) in a single move, with the leftover FL cards discarded.
   */
  placeFl: (board: Board, discards: Card[]) => void;
  /** Emit match:resign. */
  resign: () => void;
}

/**
 * Connects to the qcp-gameserver (Socket.IO, :4101) for one Chinese Poker
 * match (a multi-hand "set"). Auth on the handshake is a single-use TICKET from the
 * Limulo BFF (`lib/qcp/socket.ts`, purpose `match:<id>`): a new ticket for every
 * connection and reconnection, `wsUrl` only from the ticket response. No Logto
 * Bearer reaches the gameserver.
 *
 * The hook registers listeners for every server event defined in ServerEvents
 * and derives the client's seat identity from the first match:state projection
 * (the seat key absent from `state.opponents` is this client's own seat).
 *
 * The provably-fair deck handshake (deck:commit → deck:clientSeed) fires
 * exactly ONCE PER HAND: a `seedSentRef` flag prevents a re-emit on reconnect
 * within a hand, and is RESET at the start of each hand (on hand:start, and
 * defensively on deck:commit) so the next hand performs a fresh handshake. The
 * client seed is generated using `crypto.getRandomValues` and returned as a
 * 32-byte hex string.
 *
 * Anti-leak: this hook never reads `state.opponents[seat].draw` or
 * `state.opponents[seat].discards` — those fields do not exist on the
 * `ProjectedState.opponents` type (only `drawCount`/`discardCount` are
 * present by design). For a Fantasyland opponent the UI shows only a status +
 * count; the opponent's private FL cards are never emitted by the server.
 */
export function useQcpMatch(
  matchId: string | null,
  opts?: { vsBot?: boolean },
): QcpMatchSocket {
  const socketRef = useRef<Socket | null>(null);
  /**
   * Guards the client-seed auto-emit FOR THE CURRENT HAND: once the seed has
   * been sent for this hand (either by the auto-handshake on deck:commit /
   * hand:start, or by an explicit submitSeed call from the UI), we must NOT send
   * another one — the server expects exactly one seed per seat per HAND. Reset
   * to false at the start of every hand (hand:start / deck:commit) and on a new
   * matchId.
   */
  const seedSentRef = useRef(false);
  // Il resoconto della mano e il seed della mano dopo (v. `resocontoAperto` e `prossimaMano`). Le ref sono la verita dei gestori (che girano fuori dal render);
  // `resocontoAperto`, `prossimaManoEntro` e `attesaDistribuzione` sono lo stesso fatto per lo schermo.
  const matchIdRef = useRef<string | null>(null);
  const resocontoApertoRef = useRef(false);
  /** il `deck:commit` della mano dopo e arrivato mentre il resoconto era aperto: il seed e dovuto e parte alla chiusura */
  const seedDovutoRef = useRef(false);
  const partitaFinitaRef = useRef(false);
  /** da `hand:settled` a `deal:cards`: il seed che parte in questo intervallo e quello «dopo un resoconto» */
  const dopoResocontoRef = useRef(false);
  const timerSemeRef = useRef<number | null>(null);

  const [connected, setConnected] = useState(false);
  const [state, setState] = useState<ProjectedState | null>(null);
  const [draw, setDraw] = useState<Card[]>([]);
  const [clocks, setClocks] = useState<ClockPayload | null>(null);
  const [commitHash, setCommitHash] = useState<string | null>(null);
  const [reveal, setReveal] = useState<RevealPayload | null>(null);
  const [ended, setEnded] = useState<EndedPayload | null>(null);
  const [error, setError] = useState<ErrorPayload | null>(null);
  const [mySeat, setMySeat] = useState<Seat | null>(null);
  const [connFail, setConnFail] = useState<QcpConnFail | null>(null);
  // Bumped by retry(): re-runs the effect, i.e. a NEW ticket and a NEW socket.
  const [attempt, setAttempt] = useState(0);
  const issueTicket = useQcpIssueTicket();

  // Multi-hand "set" loop state.
  const [handNumber, setHandNumber] = useState(0);
  const [runningNet, setRunningNet] = useState<Record<Seat, number>>(ZERO_AB);
  const [inFantasyland, setInFantasyland] = useState<Record<Seat, boolean>>(FALSE_AB);
  const [flCardCount, setFlCardCount] = useState<Record<Seat, number>>(ZERO_AB);
  const [matchResult, setMatchResult] = useState<MatchResultPayload | null>(null);
  const [handSettled, setHandSettled] = useState<HandSettledPayload | null>(null);
  const [resocontoAperto, setResocontoAperto] = useState(false);
  const [prossimaManoEntro, setProssimaManoEntro] = useState<number | null>(null);
  const [attesaDistribuzione, setAttesaDistribuzione] = useState(false);

  // Capture opts.vsBot in a ref so the effect closure is stable across
  // re-renders (opts is a new object on every render otherwise).
  const vsBotRef = useRef(opts?.vsBot);
  vsBotRef.current = opts?.vsBot;

  const fermaTimerSeme = useCallback(() => {
    if (timerSemeRef.current !== null) window.clearTimeout(timerSemeRef.current);
    timerSemeRef.current = null;
    setProssimaManoEntro(null);
  }, []);

  /** Manda il seed della mano corrente, una volta sola per mano (la guardia e `seedSentRef`, che `hand:start` riabbassa). */
  const mandaSeme = useCallback((socket: Socket | null) => {
    if (!socket || seedSentRef.current || !matchIdRef.current) return;
    seedSentRef.current = true;
    if (dopoResocontoRef.current) setAttesaDistribuzione(true);
    socket.emit(ClientEvents.ClientSeed, { matchId: matchIdRef.current, seed: nuovoSeed() });
  }, []);

  /** Chiude il resoconto (tocco o timer): la mano dopo puo ricevere il suo seed. Idempotente. */
  const prossimaMano = useCallback(() => {
    fermaTimerSeme();
    if (!resocontoApertoRef.current) return;
    resocontoApertoRef.current = false;
    setResocontoAperto(false);
    if (seedDovutoRef.current) {
      seedDovutoRef.current = false;
      mandaSeme(socketRef.current);
    }
  }, [fermaTimerSeme, mandaSeme]);

  useEffect(() => {
    if (!matchId) return;

    // Reset per-match state so the hook is clean for a new matchId.
    seedSentRef.current = false;
    matchIdRef.current = matchId;
    resocontoApertoRef.current = false;
    seedDovutoRef.current = false;
    partitaFinitaRef.current = false;
    dopoResocontoRef.current = false;
    fermaTimerSeme();
    setResocontoAperto(false);
    setAttesaDistribuzione(false);
    setConnected(false);
    setState(null);
    setDraw([]);
    setClocks(null);
    setCommitHash(null);
    setReveal(null);
    setEnded(null);
    setError(null);
    setConnFail(null);
    setMySeat(null);
    setHandNumber(0);
    setRunningNet(ZERO_AB);
    setInFantasyland(FALSE_AB);
    setFlCardCount(ZERO_AB);
    setMatchResult(null);
    setHandSettled(null);

    // Called for EVERY socket `openQcpSocket` opens (the first and each reconnection, each with its own ticket).
    const attach = (socket: Socket): void => {
      socketRef.current = socket;

      // ------------------------------------------------------------------
      // Lifecycle
      // ------------------------------------------------------------------

      socket.on('connect', () => {
        setConnected(true);
        socket.emit(ClientEvents.Join, { matchId, vsBot: vsBotRef.current });
      });

      socket.on('disconnect', () => {
        setConnected(false);
      });

      // ------------------------------------------------------------------
      // Server events → React state
      // ------------------------------------------------------------------

      socket.on(ServerEvents.State, (p: ProjectedState) => {
        setState(p);

        // Derive mySeat once: the opponents map contains the OTHER seat's
        // masked entry. The key NOT in opponents is this client's seat.
        // Since seats are only 'A' | 'B', if opponents has 'B' → mySeat='A',
        // if opponents has 'A' → mySeat='B'.
        setMySeat((cur) => {
          if (cur !== null) return cur; // Already known — don't re-derive.
          const opponentKeys = Object.keys(p.opponents) as Seat[];
          if (opponentKeys.length === 0) return null; // Not enough info yet.
          const [oppSeat] = opponentKeys;
          return oppSeat === 'A' ? 'B' : 'A';
        });
      });

      // ------------------------------------------------------------------
      // Multi-hand: a new hand begins. Reset the per-hand handshake guard and
      // the per-hand draw, and adopt the hand's Fantasyland state. The fresh
      // seed handshake then fires on the upcoming deck:commit for this hand.
      // ------------------------------------------------------------------
      socket.on(ServerEvents.HandStart, (p: HandStartPayload) => {
        seedSentRef.current = false;
        setDraw([]);
        setReveal(null);
        setCommitHash(null);
        setHandNumber(p.handNumber);
        setInFantasyland(p.inFantasyland);
        setFlCardCount(p.flCardCount);
      });

      socket.on(ServerEvents.Commit, ({ commitHash: hash }: { commitHash: string }) => {
        setCommitHash(hash);

        // Auto-handshake: generate a random client seed and send it once PER
        // HAND. The server uses this to build the full commit (serverSeed xor
        // clientSeed → provably fair). Sending a second seed within the same
        // hand would be rejected, so seedSentRef guards this path (it is reset
        // on each hand:start). deck:commit also resets it defensively in case
        // the commit arrives before/without a hand:start (e.g. hand 1 opener).
        // Con un resoconto aperto il seed NON parte qui: lo tiene l'hook e parte
        // alla chiusura del resoconto (`prossimaMano`, o il timer).
        if (resocontoApertoRef.current && !partitaFinitaRef.current) {
          seedDovutoRef.current = true;
          return;
        }
        mandaSeme(socket);
      });

      socket.on(ServerEvents.Deal, (p: DealPayload) => {
        dopoResocontoRef.current = false;
        setAttesaDistribuzione(false);
        setDraw(p.cards);
      });

      // PlaceApplied — acknowledgement / animation trigger. Registered as a
      // no-op here so the listener slot is attached and can be cleaned up with
      // the socket on disconnect. The full board state arrives via the
      // follow-up match:state; animation hooks in Task 5+ can subscribe to
      // ServerEvents.PlaceApplied on the same socket independently.
      socket.on(ServerEvents.PlaceApplied, () => {
        // no-op — board state updated via match:state
      });

      socket.on(ServerEvents.Reveal, (p: RevealPayload) => {
        setReveal(p);
      });

      socket.on(ServerEvents.Ended, (p: EndedPayload) => {
        setEnded(p);
      });

      // ------------------------------------------------------------------
      // Multi-hand: a hand has settled. Update the cumulative running net and
      // expose the last reveal + per-hand result for an optional inter-hand
      // summary. The match is NOT over here (that's match:result).
      // ------------------------------------------------------------------
      socket.on(ServerEvents.HandSettled, (p: HandSettledPayload) => {
        setHandSettled(p);
        // Il resoconto si apre e il seed della mano dopo si ferma qui (v. `resocontoAperto`); se nessuno tocca «Prossima mano» parte da solo.
        resocontoApertoRef.current = true;
        seedDovutoRef.current = false;
        dopoResocontoRef.current = true;
        setResocontoAperto(true);
        fermaTimerSeme();
        // A partita finita nessuna mano segue: il resoconto resta fino al tocco, senza conto (`match:result` puo arrivare prima o dopo `hand:settled`).
        if (!partitaFinitaRef.current) {
          setProssimaManoEntro(Date.now() + SEME_AUTOMATICO_MS);
          timerSemeRef.current = window.setTimeout(() => {
            timerSemeRef.current = null;
            prossimaMano();
          }, SEME_AUTOMATICO_MS);
          // C2: se la pagina e GIA nascosta, `visibilitychange` non scatta piu e il seed resterebbe affidato al timer di sopra, che una WebView ferma puo non far partire
          // (e il server dopo ~45 s assegna la mano all'altro). Il resoconto si apre e si chiude subito; il seed parte al `deck:commit` che segue (o ora, se e gia arrivato).
          if (document.hidden) prossimaMano();
        }
        setRunningNet({ A: p.runningNetA, B: p.runningNetB });
        if (p.reveal) {
          setReveal({
            serverSeed: p.reveal.serverSeed,
            deckOrder: p.reveal.deckOrder,
            discards: p.reveal.discards,
          });
        }
      });

      // ------------------------------------------------------------------
      // Multi-hand: the set is over (emitted once). This is the canonical
      // match-end trigger for the multi-hand loop.
      // ------------------------------------------------------------------
      socket.on(ServerEvents.MatchResult, (p: MatchResultPayload) => {
        // La partita e finita: niente seed da mandare, niente conto. Il resoconto dell'ultima mano (se aperto) resta fino al tocco.
        partitaFinitaRef.current = true;
        seedDovutoRef.current = false;
        fermaTimerSeme();
        setMatchResult(p);
      });

      socket.on(ServerEvents.Clock, (p: ClockPayload) => {
        setClocks(p);
      });

      socket.on(ServerEvents.Err, (p: ErrorPayload) => {
        setError(p);
        // Auto-clear the transient error after 4 s (mirrors useBackgammonSocket).
        window.setTimeout(
          () => setError((cur) => (cur === p ? null : cur)),
          4000,
        );
      });
    };

    // The cleanup also stops a socket whose ticket has not arrived yet (strict-mode double-mount).
    const close = openQcpSocket({ purpose: `match:${matchId}`, issueTicket, onFail: setConnFail, onSocket: attach });

    return () => {
      close();
      socketRef.current = null;
      if (timerSemeRef.current !== null) window.clearTimeout(timerSemeRef.current);
      timerSemeRef.current = null;
    };
  }, [matchId, attempt, issueTicket, fermaTimerSeme, mandaSeme, prossimaMano]);

  // 🔴 C1 (ricollaudo di #725, 17/10): un telefono che mette la pagina in background con il resoconto aperto non fa scattare il timer dei 15 s (una WebView ferma i timer),
  // e il server dopo ~45 s senza il nostro seed assegna la mano all'altro (−6; all'ultima mano, la partita). Quando la pagina si NASCONDE (`visibilitychange` con
  // `document.hidden`) o si CHIUDE (`pagehide`) il resoconto si chiude SUBITO e il seed parte: al ritorno si vede la mano nuova (o «In attesa dell'avversario…»).
  // `prossimaMano()` e gia idempotente e non fa niente senza un resoconto aperto (prima mano, nessun `hand:settled`, gia chiuso: una sola volta per mano). All'ULTIMA mano
  // (`match:result` arrivato) non c'e nessun seed da mandare e il resoconto resta fino al tocco: nessun cambiamento.
  useEffect(() => {
    const quandoServe = () => {
      if (partitaFinitaRef.current) return;
      prossimaMano();
    };
    const nascosta = () => {
      if (document.hidden) quandoServe();
    };
    document.addEventListener('visibilitychange', nascosta);
    window.addEventListener('pagehide', quandoServe);
    return () => {
      document.removeEventListener('visibilitychange', nascosta);
      window.removeEventListener('pagehide', quandoServe);
    };
  }, [prossimaMano]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // ------------------------------------------------------------------
  // Stable emit helper
  // ------------------------------------------------------------------

  const emit = useCallback((event: string, payload: unknown) => {
    socketRef.current?.emit(event, payload);
  }, []);

  // ------------------------------------------------------------------
  // Public imperative API
  // ------------------------------------------------------------------

  return {
    connected,
    state,
    draw,
    clocks,
    commitHash,
    reveal,
    ended,
    error,
    connFail,
    retry,
    mySeat,
    handNumber,
    runningNet,
    inFantasyland,
    flCardCount,
    matchResult,
    handSettled,
    resocontoAperto,
    prossimaManoEntro,
    prossimaMano,
    attesaDistribuzione,

    submitSeed: useCallback(
      (seed: string) => {
        // Enforce the seed-once-per-hand invariant at the hook level: the server
        // accepts exactly one client seed per seat per HAND — a second emission
        // within the same hand is rejected. The auto-handshake (on deck:commit)
        // normally fires this; a manual call is only a no-op safety net within
        // the hand and must not double-emit. The guard is reset on hand:start.
        if (seedSentRef.current) return;
        seedSentRef.current = true;
        emit(ClientEvents.ClientSeed, { matchId, seed });
      },
      [emit, matchId],
    ),

    place: useCallback(
      (placements: { card: Card; row: Row }[], discard: Card | null) => {
        if (!matchId) return;
        emit(ClientEvents.Place, { matchId, placements, discard } satisfies PlaceSubmit);
      },
      [emit, matchId],
    ),

    placeFl: useCallback(
      (board: Board, discards: Card[]) => {
        if (!matchId) return;
        emit(ClientEvents.PlaceFl, {
          matchId,
          placements: board,
          discards,
        } satisfies PlaceFlSubmit);
      },
      [emit, matchId],
    ),

    resign: useCallback(() => {
      emit(ClientEvents.Resign, { matchId });
    }, [emit, matchId]),
  };
}
